import { ExporterReceiversSection } from '../../components/settings/ExporterReceivers';
import { ExporterLabelsSection } from '../../components/settings/ExporterLabels';
import { BoxFormatMappingSection } from '../../components/frutic-box-mapping/FruticBoxMapping';
import { ReceiverMappingSection } from '../../components/receiver-mapping/ReceiverMapping';
import { PlanillaManagementSection } from '../../components/settings/PlanillaManagement';
import { MarketComparisonConfigSection } from '../../components/settings/MarketComparisonConfig';
import { FavoriteExportersSection } from '../../components/settings/FavoriteExporters';
import { FavoriteBrandsSection } from '../../components/settings/FavoriteBrands';
import { DataMigrationSection } from '../../components/settings/DataMigrationSection';
import { ExporterCostCategoriesSection } from '../../components/settings/ExporterCostCategories';
import Header from '../../components/layouts/Header';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import useUserStore from '../../../../common/src/stores/useUserStore';
import {
  getCustomCostCategoriesEnabled,
  getUnclassifiedExporterCosts,
} from 'common/src/api/exporter-cost-categories';
import { Chip, Divider, Spinner, Tabs, Tab } from '@nextui-org/react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { PageLayout } from '../../components/ui';
import CaseReasons from '../shipment-cases/CaseReasons';
import CaseExternalEntities from '../shipment-cases/CaseExternalEntities';

export default function SettingsPage() {
  const { t } = useTranslation('settings');
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const { userProfile } = useUserStore();
  // null while loading, so a #cost-categories link can wait for the flag.
  const [costCategoriesEnabled, setCostCategoriesEnabled] = useState<
    boolean | null
  >(null);
  const [unclassifiedCostCount, setUnclassifiedCostCount] = useState(0);
  const [location] = useLocation();
  const [activeTab, setActiveTab] = useState<
    | 'clients'
    | 'labels-formats'
    | 'planillas'
    | 'market-comparison'
    | 'tracking'
    | 'migrations'
    | 'cost-categories'
    | 'claims'
  >('clients');
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!userProfile?.exporter_id) return;
    const exporterId = userProfile.exporter_id;
    getCustomCostCategoriesEnabled(exporterId)
      .then(async (enabled) => {
        setCostCategoriesEnabled(enabled);
        if (enabled) {
          const unclassified = await getUnclassifiedExporterCosts(exporterId);
          setUnclassifiedCostCount(unclassified.length);
        }
      })
      .catch((error) =>
        console.error('Error loading cost categories flag:', error)
      );
  }, [userProfile?.exporter_id]);

  useEffect(() => {
    if (costCategoriesEnabled === false && activeTab === 'cost-categories') {
      setActiveTab('clients');
    }
  }, [activeTab, costCategoriesEnabled]);

  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      if (hash === '#packing-codes-mapping' || hash === '#labels') {
        setActiveTab('labels-formats');
        let attempts = 0;
        const maxAttempts = 20;
        const intervalMs = 150;
        const intervalId = window.setInterval(() => {
          const targetId =
            hash === '#labels' ? 'labels' : 'packing-codes-mapping';
          const target = document.getElementById(targetId);
          const container = scrollContainerRef.current;
          if (target && container) {
            const containerRect = container.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            const offset =
              targetRect.top - containerRect.top + container.scrollTop;
            container.scrollTo({
              top: Math.max(offset - 16, 0),
              behavior: 'smooth',
            });
            window.clearInterval(intervalId);
            return;
          }
          attempts += 1;
          if (attempts >= maxAttempts) {
            window.clearInterval(intervalId);
          }
        }, intervalMs);
        return () => window.clearInterval(intervalId);
      }

      // Any tab can be opened directly (VictorIA links to /settings#tracking).
      const tab = hash.slice(1);
      if (
        tab === 'clients' ||
        tab === 'labels-formats' ||
        tab === 'tracking' ||
        tab === 'market-comparison' ||
        tab === 'migrations' ||
        tab === 'planillas' ||
        tab === 'cost-categories' ||
        tab === 'claims'
      ) {
        setActiveTab(tab);
      }
    };

    const cleanup = handleHash();
    window.addEventListener('hashchange', handleHash);
    return () => {
      if (cleanup) cleanup();
      window.removeEventListener('hashchange', handleHash);
    };
  }, [location]);

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <PageLayout className='overflow-y-auto' ref={scrollContainerRef}>
        <Header
          title={t('Configuración')}
          description={t('Configuración de tu plataforma')}
        />

        <Tabs
          aria-label='Configuración'
          className='mb-4'
          selectedKey={activeTab}
          onSelectionChange={(key) =>
            setActiveTab(String(key) as typeof activeTab)
          }
        >
          <Tab key='clients' title={t('Clientes')} />
          <Tab key='labels-formats' title={t('Etiquetas y Formatos')} />
          <Tab key='tracking' title={t('Seguimiento Mercado')} />
          <Tab key='market-comparison' title={t('Comparación vs Mercado')} />
          <Tab key='migrations' title={t('Estandarización de Datos')} />
          <Tab key='planillas' title={t('Planillas')} />
          <Tab key='claims' title={t('Reclamos', { ns: 'shipmentCases' })} />
          {(costCategoriesEnabled || activeTab === 'cost-categories') && (
            <Tab
              key='cost-categories'
              title={
                <div className='flex items-center gap-2'>
                  {t('Categorías de costos')}
                  {unclassifiedCostCount > 0 && (
                    <Chip size='sm' color='warning' variant='flat'>
                      {unclassifiedCostCount}
                    </Chip>
                  )}
                </div>
              }
            />
          )}
        </Tabs>

        <div className='pt-4'>
          {activeTab === 'clients' && (
            <>
              <ExporterReceiversSection />
              <Divider className='my-4' />
              <p className='text-lg font-bold'>{t('Mapeo de Clientes')}</p>
              <ReceiverMappingSection
                speciesId={selectedSpecies?.id || 0}
                season={selectedSeason}
                hide={false}
              />
            </>
          )}

          {activeTab === 'labels-formats' && (
            <>
              <div id='labels'>
                <ExporterLabelsSection
                  speciesId={selectedSpecies?.id || 0}
                  season={selectedSeason}
                />
              </div>
              <div id='packing-codes-mapping'>
                <Divider className='my-4' />
                <BoxFormatMappingSection
                  speciesId={selectedSpecies?.id || 0}
                  season={selectedSeason}
                  exporterRef=''
                  hide={false}
                />
              </div>
            </>
          )}

          {activeTab === 'tracking' && (
            <>
              <FavoriteBrandsSection />
              <Divider className='my-4' />
              <FavoriteExportersSection />
            </>
          )}

          {activeTab === 'market-comparison' && (
            <MarketComparisonConfigSection />
          )}

          {activeTab === 'migrations' && <DataMigrationSection />}

          {activeTab === 'planillas' && <PlanillaManagementSection />}
          {activeTab === 'claims' && userProfile?.exporter_id && (
            <div className='flex flex-col gap-6'>
              <CaseReasons
                exporterId={Number(userProfile.exporter_id)}
                identity={JSON.stringify([
                  userProfile.auth_id,
                  userProfile.id,
                  userProfile.exporter_id,
                ])}
              />
              <Divider />
              <CaseExternalEntities
                exporterId={Number(userProfile.exporter_id)}
                identity={JSON.stringify([
                  userProfile.auth_id,
                  userProfile.id,
                  userProfile.exporter_id,
                ])}
              />
            </div>
          )}

          {activeTab === 'cost-categories' &&
            (costCategoriesEnabled ? (
              <ExporterCostCategoriesSection
                onUnclassifiedCountChange={setUnclassifiedCostCount}
              />
            ) : (
              <Spinner size='sm' />
            ))}
        </div>
      </PageLayout>
    </div>
  );
}
